import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ArrowRight, Lifebuoy, PaperPlaneTilt, Plus } from "@phosphor-icons/react";
import { publicFaqQuery } from "@/api-queries/faq";
import { DEFAULT_FAQ_ICON, FAQ_ICONS, type FaqIconKey } from "@/lib/faq-icons";
import { filterFaqCategories, type FaqPlacement } from "@/lib/faq-categories";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { defineSection } from "@/ssr/define-section";
import type { PublicFaqCategory } from "@/types/faq";

function pickLang(lang: string): "Bs" | "En" {
  return lang?.toLowerCase().startsWith("en") ? "En" : "Bs";
}

export interface PublicFaqSectionProps {
  /** Stranica za koju se biraju kategorije; admin je postavlja po kategoriji. */
  placement: FaqPlacement;
  copyKey?: string;
  sectionId?: string;
  background?: string;
  /** Render a single flat list of all questions with no category navigation. */
  flat?: boolean;
}

function PublicFaqSectionView({
  placement,
  copyKey = "home.faq",
  sectionId,
  background = "oklch(100% 0 0)",
  flat = false,
}: PublicFaqSectionProps) {
  const ref = useScrollReveal<HTMLElement>();
  const { t, i18n } = useTranslation();
  const suffix = pickLang(i18n.language);

  const { data } = useQuery(publicFaqQuery);
  const categories: PublicFaqCategory[] = filterFaqCategories(
    data?.categories ?? [],
    placement,
  );

  const [activeCatId, setActiveCatId] = useState<string | null>(null);
  const [openIndex, setOpenIndex] = useState<number>(0);

  if (categories.length === 0) return null;

  const activeCat = categories.find((c) => c.id === activeCatId) ?? categories[0]!;
  const showCategoryNav = categories.length > 1 && !flat;
  const catName = (c: PublicFaqCategory) => (suffix === "En" ? c.nameEn : c.nameBs);
  const sourceItems = flat ? categories.flatMap((c) => c.items) : activeCat.items;
  const items = sourceItems.map((it) => ({
    q: suffix === "En" ? it.questionEn : it.questionBs,
    a: suffix === "En" ? it.answerEn : it.answerBs,
  }));

  const selectCat = (id: string) => {
    setActiveCatId(id);
    setOpenIndex(0);
  };

  const renderIcon = (
    icon: FaqIconKey,
    props: { size: number; weight?: "fill" | "bold"; style?: React.CSSProperties },
  ) => {
    const Icon = FAQ_ICONS[icon] ?? FAQ_ICONS[DEFAULT_FAQ_ICON];
    return <Icon {...props} />;
  };

  return (
    <section
      ref={ref}
      id={sectionId}
      className="relative overflow-hidden py-24 sm:py-32"
      style={{ background }}
    >
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          {flat ? (
            <h2
              className="mx-auto max-w-4xl font-black leading-[0.98] tracking-[-0.035em] text-foreground"
              style={{ fontFamily: "var(--font-heading)", fontSize: "clamp(2.25rem, 4.5vw, 3.5rem)" }}
            >
              {t(`${copyKey}.title`)}
            </h2>
          ) : (
            <>
              {t(`${copyKey}.badge`) && (
                <span
                  className="text-[11px] font-bold uppercase tracking-[0.14em]"
                  style={{ color: "oklch(37.12% 0.059 186.62)" }}
                >
                  {t(`${copyKey}.badge`)}
                </span>
              )}
              <h2
                className="mx-auto mt-3 max-w-4xl font-black leading-[0.98] tracking-[-0.035em] text-foreground"
                style={{ fontFamily: "var(--font-heading)", fontSize: "clamp(2.25rem, 4.5vw, 3.5rem)" }}
              >
                {t(`${copyKey}.titlePrefix`)}{" "}
                <em
                  className="font-normal italic"
                  style={{ fontFamily: "var(--font-heading)", color: "oklch(37.12% 0.059 186.62)" }}
                >
                  {t(`${copyKey}.titleHighlight`)}
                </em>
                {t(`${copyKey}.titleSuffix`) && (
                  <>
                    <br className="hidden sm:block" />
                    <span className="sm:ml-0"> {t(`${copyKey}.titleSuffix`)}</span>
                  </>
                )}
              </h2>
            </>
          )}
          {!flat && t(`${copyKey}.subtitle`) && (
            <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-muted-foreground">
              {t(`${copyKey}.subtitle`)}
            </p>
          )}
        </div>

        {showCategoryNav && (
          <div className="mt-12 -mx-4 overflow-x-auto px-4 lg:hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <div className="inline-flex gap-2 pr-4">
              {categories.map((c) => {
                const isActive = c.id === activeCat.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => selectCat(c.id)}
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border-[1.5px] px-3.5 py-2.5 text-xs font-bold transition-colors"
                    style={{
                      background: isActive ? "oklch(37.12% 0.059 186.62)" : "white",
                      borderColor: isActive ? "oklch(37.12% 0.059 186.62)" : "oklch(82% 0.03 84)",
                      color: isActive ? "oklch(98% 0 0)" : "oklch(18% 0.02 84)",
                    }}
                  >
                    {renderIcon(c.icon, {
                      size: 14,
                      weight: "fill",
                      style: { color: isActive ? "oklch(98% 0 0)" : "oklch(74.72% 0.089 82.39)" },
                    })}
                    {catName(c)}
                    <span className="text-[10px] opacity-65">
                      {String(c.items.length).padStart(2, "0")}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div
          className={
            showCategoryNav
              ? "mt-10 grid gap-12 lg:mt-14 lg:grid-cols-[260px_1fr] lg:gap-16"
              : "mx-auto mt-10 max-w-4xl lg:mt-14"
          }
        >
          {showCategoryNav && (
            <aside className="hidden self-start lg:sticky lg:top-32 lg:block">
              <h4
                className="mb-4 border-b pb-3 text-[11px] font-extrabold uppercase tracking-[0.14em]"
                style={{ color: "oklch(45% 0.02 84)", borderColor: "oklch(82% 0.03 84)" }}
              >
                {t(`${copyKey}.categoriesLabel`)}
              </h4>
              <ul className="flex flex-col gap-0.5">
                {categories.map((c) => {
                  const isActive = c.id === activeCat.id;
                  return (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => selectCat(c.id)}
                        className="flex w-full items-center justify-between gap-2.5 rounded-lg px-3.5 py-2.5 text-left text-sm font-semibold transition-colors"
                        style={{
                          background: isActive ? "oklch(37.12% 0.059 186.62)" : "transparent",
                          color: isActive ? "oklch(98% 0 0)" : "oklch(18% 0.02 84)",
                        }}
                        onMouseOver={(e) => {
                          if (!isActive) e.currentTarget.style.background = "rgba(0,0,0,0.04)";
                        }}
                        onMouseOut={(e) => {
                          if (!isActive) e.currentTarget.style.background = "transparent";
                        }}
                      >
                        <span className="inline-flex items-center gap-2.5">
                          {renderIcon(c.icon, {
                            size: 16,
                            weight: "fill",
                            style: { color: isActive ? "oklch(98% 0 0)" : "oklch(74.72% 0.089 82.39)" },
                          })}
                          {catName(c)}
                        </span>
                        <span
                          className="text-[11px] font-bold"
                          style={{ color: isActive ? "oklch(98% 0 0)" : "oklch(45% 0.02 84)", opacity: isActive ? 0.6 : 1 }}
                        >
                          {String(c.items.length).padStart(2, "0")}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>

              <HelpCard copyKey={copyKey} compact />
            </aside>
          )}

          <div>
            {!flat && (
              <div className="mb-5 flex items-baseline justify-between gap-4">
                <h3
                  className="italic"
                  style={{
                    fontFamily: "var(--font-heading)",
                    fontWeight: 400,
                    fontSize: "clamp(1.75rem, 3vw, 2.25rem)",
                    letterSpacing: "-0.01em",
                    color: "oklch(37.12% 0.059 186.62)",
                  }}
                >
                  {catName(activeCat)}
                </h3>
                <span
                  className="text-right text-[11px] font-bold uppercase tracking-[0.12em]"
                  style={{ color: "oklch(45% 0.02 84)" }}
                >
                  {t(`${copyKey}.questionsLabel`, { count: items.length })}
                </span>
              </div>
            )}

            <div>
              {items.map((item, i) => {
                const isOpen = i === openIndex;
                return (
                  <div
                    key={`${activeCat.id}-${i}`}
                    className="border-t py-5 sm:py-6"
                    style={{
                      borderColor: "oklch(82% 0.03 84)",
                      ...(i === items.length - 1 ? { borderBottomWidth: "1px", borderBottomStyle: "solid" } : {}),
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenIndex(isOpen ? -1 : i)}
                      className="flex w-full items-start justify-between gap-5 text-left"
                    >
                      <h4 className="text-base font-bold leading-snug tracking-[-0.005em] sm:text-lg">{item.q}</h4>
                      <span
                        className="flex size-7 flex-shrink-0 items-center justify-center rounded-full border-[1.5px] transition-all sm:size-8"
                        style={{
                          borderColor: "oklch(37.12% 0.059 186.62)",
                          background: isOpen ? "oklch(37.12% 0.059 186.62)" : "transparent",
                          color: isOpen ? "oklch(98% 0 0)" : "oklch(37.12% 0.059 186.62)",
                        }}
                      >
                        <Plus
                          size={14}
                          weight="bold"
                          style={{ transition: "transform 0.2s", transform: isOpen ? "rotate(45deg)" : "rotate(0deg)" }}
                        />
                      </span>
                    </button>
                    {isOpen && (
                      <p className="mt-3.5 max-w-2xl whitespace-pre-line text-sm leading-[1.65] text-muted-foreground sm:text-[15px]">
                        {item.a}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            {!flat && (
              <HelpCard copyKey={copyKey} className={showCategoryNav ? "lg:hidden" : ""} />
            )}

            <div className="mt-10 flex justify-end">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 text-sm font-bold transition-colors hover:opacity-70"
                style={{ color: "oklch(37.12% 0.059 186.62)" }}
              >
                {t(`${copyKey}.viewAll`)}
                <ArrowRight size={16} weight="bold" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function HelpCard({
  copyKey,
  compact = false,
  className = "",
}: {
  copyKey: string;
  compact?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();

  if (compact) {
    return (
      <div
        className="mt-6 rounded-2xl border border-dashed p-5"
        style={{ background: "rgba(0,0,0,0.04)", borderColor: "oklch(82% 0.03 84)" }}
      >
        <div className="mb-1.5 flex items-center gap-2">
          <Lifebuoy size={18} weight="fill" style={{ color: "oklch(74.72% 0.089 82.39)" }} />
          <strong className="text-[13px]">{t(`${copyKey}.helpTitle`)}</strong>
        </div>
        <p className="mb-3 text-xs leading-relaxed text-muted-foreground">{t(`${copyKey}.helpBody`)}</p>
        <Link
          to="/kontakt"
          className="inline-flex items-center gap-1.5 rounded-full border-[1.5px] px-4 py-2 text-xs font-bold transition-colors"
          style={{ borderColor: "oklch(82% 0.03 84)" }}
        >
          <PaperPlaneTilt size={14} />
          {t(`${copyKey}.helpCta`)}
        </Link>
      </div>
    );
  }

  return (
    <div
      className={`mt-8 rounded-2xl border border-dashed p-6 text-center ${className}`}
      style={{ background: "rgba(0,0,0,0.04)", borderColor: "oklch(82% 0.03 84)" }}
    >
      <div
        className="mx-auto mb-3 inline-flex size-10 items-center justify-center rounded-full border-[1.5px] bg-white"
        style={{ borderColor: "oklch(82% 0.03 84)" }}
      >
        <Lifebuoy size={20} weight="fill" style={{ color: "oklch(74.72% 0.089 82.39)" }} />
      </div>
      <strong className="block text-sm">{t(`${copyKey}.helpTitle`)}</strong>
      <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">{t(`${copyKey}.helpBody`)}</p>
      <Link
        to="/kontakt"
        className="mt-3.5 inline-flex items-center gap-1.5 rounded-full px-4 py-2.5 text-xs font-bold"
        style={{ background: "oklch(37.12% 0.059 186.62)", color: "oklch(98% 0 0)" }}
      >
        <PaperPlaneTilt size={14} />
        {t(`${copyKey}.helpCta`)}
      </Link>
    </div>
  );
}

export const PublicFaqSection = defineSection<PublicFaqSectionProps>({
  Component: PublicFaqSectionView,
  queries: [publicFaqQuery],
});
